import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

const RESIZE_HANDLE_WIDTH = 6
const COLLAPSED_STRIP_WIDTH = 40

export interface ResizableNPanelLayoutPanel {
  id: string
  /**
   * Optional identity hint -- if the caller swaps the underlying panel
   * kind while reusing the same `id`, supplying `component` lets the
   * renderer detect the change and reset its width/collapse state.
   */
  component?: string
  content: ReactNode
  /**
   * Dimensionless ratio. The available container width (after reserving
   * each panel's `minWidthRem`) is distributed across all visible panels
   * proportionally to their `weight`. The trailing panel's `weight` applies
   * to fresh-distribute allocations (mount, pre-drag container resize,
   * pre-drag collapse toggle); after the first user drag, the trailing
   * panel becomes a pure runtime-slack absorber. Must be > 0 and finite.
   */
  weight: number
  /** Floor on the panel's width, in rem. Must be > 0 and finite. */
  minWidthRem: number
  /**
   * Panels sharing a non-empty `collapseGroup` collapse together -- the
   * entire group is replaced by a single 40px strip with a chevron toggle
   * when collapsed (NOT N * 40px). Panels in a group must be contiguous
   * in `panels[]`. Only fixed panels may participate (the rightmost flex
   * panel cannot be in a collapse group).
   */
  collapseGroup?: string
}

interface ResizableNPanelLayoutProps {
  panels: ReadonlyArray<ResizableNPanelLayoutPanel>
}

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.forecast.background1};
  overflow: hidden;
  border-radius: 0.5rem;

  @media print {
    overflow: visible;
    height: auto;
    border-radius: 0;
  }
`

const FixedPanel = styled.div<{
  $width: number
  $minWidth: number
}>`
  width: ${({ $width }) => $width}px;
  min-width: ${({ $minWidth }) => $minWidth}px;
  background-color: ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;

  @media print {
    display: none;
  }
`

const FlexPanel = styled.div<{ $minWidth: number }>`
  flex: 1 1 auto;
  min-width: ${({ $minWidth }) => $minWidth}px;
  background-color: ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: auto;
  min-height: 0;

  @media print {
    width: 100% !important;
    overflow: visible;
    height: auto;
  }
`

const ResizeHandle = styled.div`
  width: ${RESIZE_HANDLE_WIDTH}px;
  background-color: ${({ theme }) => theme.forecast.border2};
  cursor: col-resize;
  position: relative;
  flex-shrink: 0;
  transition: background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: ${({ theme }) => theme.forecast.secondaryText};
    border-radius: 1px;
    opacity: 0.5;
    transition: all 0.2s ease;
  }

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary}20;
    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.primary}40;
  }

  @media print {
    display: none;
  }
`

const CollapsedStrip = styled.button`
  width: ${COLLAPSED_STRIP_WIDTH}px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.forecast.background1};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.forecast.border2};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) =>
      theme.forecast.hoverButton ?? 'rgba(0, 0, 0, 0.04)'};
    color: ${({ theme }) => theme.forecast.activeText};
  }

  @media print {
    display: none;
  }
`

/**
 * Resize handle that doubles as a collapse trigger. Hosted between a
 * collapsible group's last panel and the next element. Renders a chevron
 * overlay sitting on top of the drag handle; clicking the chevron toggles
 * collapse, dragging the rest of the handle resizes.
 */
const CollapsibleResizeHandle = styled(ResizeHandle)`
  position: relative;
`

const CollapseChevronOverlay = styled.button`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 18px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.secondaryText};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 1;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) =>
      theme.forecast.hoverButton ?? 'rgba(0, 0, 0, 0.04)'};
    color: ${({ theme }) => theme.forecast.activeText};
  }

  @media print {
    display: none;
  }
`

const CollapsibleGroupWrapper = styled.div`
  display: contents;
`

/**
 * Generic N-panel resizable side-by-side layout. The last panel is the
 * flexible remainder; all earlier panels are fixed-width with mouse-driven
 * draggable handles between them.
 *
 * **Sizing model**: each panel declares a dimensionless `weight` plus a
 * `minWidthRem` floor. At mount and on container resize (before any user
 * drag), the renderer distributes the container's available width across
 * all visible panels proportionally to their weights, after reserving each
 * panel's minimum. The trailing panel renders as a CSS flex remainder, so
 * its weight only affects fresh allocations -- once the user drags a
 * handle, the trailing panel becomes a pure slack absorber and subsequent
 * container resizes shrink iteratively rather than re-distributing.
 *
 * **Collapse groups**: panels sharing a non-empty `collapseGroup` collapse
 * together -- when collapsed, the whole group is replaced by a single 40px
 * strip with a chevron-expand button. Panels in a group must be contiguous
 * in the panels array. Only fixed panels may participate (the trailing
 * flex panel is never collapsible).
 *
 * **Print**: doc-nav / viewer / handles hide; the flex panel expands to
 * full width.
 */
export const ResizableNPanelLayout: FC<ResizableNPanelLayoutProps> = ({
  panels,
}) => {
  if (panels.length < 2) {
    return <SinglePanel panels={panels} />
  }

  return <ResizableContent panels={panels} />
}

const SinglePanel: FC<ResizableNPanelLayoutProps> = ({ panels }) => {
  // Use the same identity-key + live root font size path as the N-panel
  // case so rem sizing honors non-default root font sizes.
  const identityKey = useMemo(
    () => panels.map((p) => `${p.id}:${p.minWidthRem}`).join('|'),
    [panels]
  )
  const remPx = useRemPx(identityKey)
  return (
    <Container>
      {panels.map((panel) => (
        <FlexPanel key={panel.id} $minWidth={panel.minWidthRem * remPx}>
          {panel.content}
        </FlexPanel>
      ))}
    </Container>
  )
}

export interface PanelGroup {
  /** Group id -- the `collapseGroup` string, or `null` for ungrouped panels. */
  collapseGroup: string | null
  /** Indices into `fixedPanels` for the panels in this group. */
  indices: number[]
}

export function groupContiguousByCollapseGroup(
  fixedPanels: ReadonlyArray<ResizableNPanelLayoutPanel>
): PanelGroup[] {
  const groups: PanelGroup[] = []
  for (let i = 0; i < fixedPanels.length; i++) {
    const panel = fixedPanels[i]
    const group = panel.collapseGroup ?? null
    const last = groups[groups.length - 1]
    if (last && last.collapseGroup !== null && last.collapseGroup === group) {
      last.indices.push(i)
    } else {
      groups.push({ collapseGroup: group, indices: [i] })
    }
  }
  return groups
}

/* ============================================================
 * Width-allocation helpers
 *
 * Four small functions cooperate to keep mount, container-resize,
 * collapse-toggle, and drag clamp in sync:
 *
 *   computeLayoutBudget    -- shared primitive: handle/strip counts,
 *                             which fixed panels are currently visible.
 *   allocatePanelWidths    -- fresh-distribute by weight (mount + pre-drag).
 *   clampWidthsToFit       -- iterative min-saturating shrink (post-drag).
 *   maxWidthForFixedPanel  -- per-drag clamp inside onPointerMove.
 *
 * All four route through computeLayoutBudget so the handle/strip
 * accounting agrees by construction. Exported for direct unit testing.
 * ============================================================ */

export interface LayoutBudget {
  handlesPx: number
  stripsPx: number
  /** Indices into `fixedPanels` that are currently visible (not inside a collapsed group). */
  visibleFixedIndices: number[]
}

export function computeLayoutBudget(args: {
  fixedPanels: ReadonlyArray<ResizableNPanelLayoutPanel>
  groups: ReadonlyArray<PanelGroup>
  collapsedGroups: ReadonlySet<string>
}): LayoutBudget {
  const { groups, collapsedGroups } = args
  let handleCount = 0
  let collapsedStripCount = 0
  const visibleFixedIndices: number[] = []
  for (const g of groups) {
    const isCollapsed =
      g.collapseGroup !== null && collapsedGroups.has(g.collapseGroup)
    if (isCollapsed) {
      collapsedStripCount += 1
    } else {
      // Each visible group renders `length` handles: (length - 1) internal
      // between its panels + 1 trailing after the last panel.
      handleCount += g.indices.length
      for (const idx of g.indices) visibleFixedIndices.push(idx)
    }
  }
  return {
    handlesPx: handleCount * RESIZE_HANDLE_WIDTH,
    stripsPx: collapsedStripCount * COLLAPSED_STRIP_WIDTH,
    visibleFixedIndices,
  }
}

export interface AllocationResult {
  widths: number[]
  warn: string | null
}

/**
 * Fresh-distribute allocation: distributes container width across all
 * visible panels (fixed + trailing) proportionally to weight, after
 * reserving each panel's minWidthRem floor. Returns widths only for the
 * fixed panels (the trailing panel absorbs the remainder via flex CSS by
 * construction). Hidden panels keep their prevWidths entry.
 */
export function allocatePanelWidths(args: {
  fixedPanels: ReadonlyArray<ResizableNPanelLayoutPanel>
  flexPanel: ResizableNPanelLayoutPanel
  groups: ReadonlyArray<PanelGroup>
  collapsedGroups: ReadonlySet<string>
  containerWidth: number
  remPx: number
  prevWidths: ReadonlyArray<number>
}): AllocationResult {
  const {
    fixedPanels,
    flexPanel,
    groups,
    collapsedGroups,
    containerWidth,
    remPx,
    prevWidths,
  } = args
  const budget = computeLayoutBudget({ fixedPanels, groups, collapsedGroups })
  const widths = [...prevWidths]

  // Degenerate: no visible fixed panels (single-panel layout, or all
  // fixed panels currently collapsed). Skip distribution; the trailing
  // flex panel absorbs everything via CSS.
  if (budget.visibleFixedIndices.length === 0) {
    return { widths, warn: null }
  }

  const available = containerWidth - budget.handlesPx - budget.stripsPx
  const flexMinPx = flexPanel.minWidthRem * remPx
  const sumVisibleMinPx = budget.visibleFixedIndices.reduce(
    (sum, idx) => sum + fixedPanels[idx].minWidthRem * remPx,
    0
  )
  const slack = available - sumVisibleMinPx - flexMinPx

  if (slack < 0) {
    // Mins genuinely don't fit -- saturate fixed panels at their mins,
    // trailing flex panel takes its own min, content clips beyond.
    for (const idx of budget.visibleFixedIndices) {
      widths[idx] = fixedPanels[idx].minWidthRem * remPx
    }
    return {
      widths,
      warn: 'Container too narrow for panel minWidthRem totals; content will clip.',
    }
  }

  // Distribute slack across ALL visible panels (fixed + trailing) by
  // weight. Trailing target is computed but not committed -- flex: 1 1
  // auto absorbs the remainder, which equals its computed target.
  const totalWeight =
    budget.visibleFixedIndices.reduce(
      (sum, idx) => sum + fixedPanels[idx].weight,
      0
    ) + flexPanel.weight

  for (const idx of budget.visibleFixedIndices) {
    const panel = fixedPanels[idx]
    const minPx = panel.minWidthRem * remPx
    widths[idx] = minPx + (panel.weight / totalWeight) * slack
  }
  return { widths, warn: null }
}

/**
 * Drag-preserving shrink: if existing widths overflow the container,
 * iteratively shrink the above-min panels proportionally, saturating any
 * panel that would dip below its min and redistributing the remaining
 * shrink across the smaller pool. Does NOT redistribute by weight --
 * preserves the user's drag intent on grow.
 */
export function clampWidthsToFit(args: {
  fixedPanels: ReadonlyArray<ResizableNPanelLayoutPanel>
  flexPanel: ResizableNPanelLayoutPanel
  groups: ReadonlyArray<PanelGroup>
  collapsedGroups: ReadonlySet<string>
  containerWidth: number
  remPx: number
  currentWidths: ReadonlyArray<number>
}): AllocationResult {
  const {
    fixedPanels,
    flexPanel,
    groups,
    collapsedGroups,
    containerWidth,
    remPx,
    currentWidths,
  } = args
  const budget = computeLayoutBudget({ fixedPanels, groups, collapsedGroups })
  const widths = [...currentWidths]

  if (budget.visibleFixedIndices.length === 0) {
    return { widths, warn: null }
  }

  const flexMinPx = flexPanel.minWidthRem * remPx
  const sumVisibleWidths = budget.visibleFixedIndices.reduce(
    (sum, idx) => sum + widths[idx],
    0
  )
  const excess =
    sumVisibleWidths +
    flexMinPx +
    budget.handlesPx +
    budget.stripsPx -
    containerWidth

  if (excess <= 0) return { widths, warn: null }

  // Iterative min-saturating shrink.
  let remaining = excess
  // Pool: indices currently above their min.
  const pool = new Set(budget.visibleFixedIndices)
  while (remaining > 0 && pool.size > 0) {
    const poolWidths = [...pool].reduce(
      (sum, idx) => sum + widths[idx] - fixedPanels[idx].minWidthRem * remPx,
      0
    )
    if (poolWidths <= 0) break
    // Shrink each pool panel by its share of remaining (proportional to
    // its current above-min slack).
    let absorbedThisRound = 0
    for (const idx of [...pool]) {
      const minPx = fixedPanels[idx].minWidthRem * remPx
      const aboveMin = widths[idx] - minPx
      if (aboveMin <= 0) {
        pool.delete(idx)
        continue
      }
      const share = (aboveMin / poolWidths) * remaining
      const newWidth = widths[idx] - share
      if (newWidth <= minPx) {
        absorbedThisRound += aboveMin // saturated to min
        widths[idx] = minPx
        pool.delete(idx)
      } else {
        absorbedThisRound += share
        widths[idx] = newWidth
      }
    }
    if (absorbedThisRound <= 0) break
    remaining -= absorbedThisRound
  }

  if (remaining > 1) {
    return {
      widths,
      warn: 'Container too narrow even after shrinking all panels to minWidthRem; content will clip.',
    }
  }
  return { widths, warn: null }
}

/**
 * Per-drag clamp: returns the maximum allowed width for the panel being
 * dragged so the drag never pushes total content past the container.
 * Replaces the ad-hoc budget math previously inlined in onPointerMove.
 */
export function maxWidthForFixedPanel(args: {
  fixedPanels: ReadonlyArray<ResizableNPanelLayoutPanel>
  flexPanel: ResizableNPanelLayoutPanel
  groups: ReadonlyArray<PanelGroup>
  collapsedGroups: ReadonlySet<string>
  containerWidth: number
  remPx: number
  currentWidths: ReadonlyArray<number>
  draggedIndex: number
}): number {
  const {
    fixedPanels,
    flexPanel,
    groups,
    collapsedGroups,
    containerWidth,
    remPx,
    currentWidths,
    draggedIndex,
  } = args
  const budget = computeLayoutBudget({ fixedPanels, groups, collapsedGroups })
  const flexMinPx = flexPanel.minWidthRem * remPx
  const otherVisibleWidth = budget.visibleFixedIndices.reduce(
    (sum, idx) => (idx === draggedIndex ? sum : sum + currentWidths[idx]),
    0
  )
  const draggedMin = fixedPanels[draggedIndex].minWidthRem * remPx
  return Math.max(
    draggedMin,
    containerWidth -
      otherVisibleWidth -
      flexMinPx -
      budget.handlesPx -
      budget.stripsPx
  )
}

/* ============================================================
 * Hooks
 * ============================================================ */

/**
 * Reads the root font size on mount and exposes the px-per-rem ratio.
 * Refreshes only when its identity-key dependency changes (i.e. layout
 * remount via `panelIdentityKey`). Runtime root-font-size changes are
 * rare and would cause a top-level app re-render anyway.
 */
function useRemPx(identityKey: string): number {
  const [remPx, setRemPx] = useState(() => readRemPx())
  useEffect(() => {
    setRemPx(readRemPx())
  }, [identityKey])
  return remPx
}

function readRemPx(): number {
  if (typeof window === 'undefined') return 16
  const computed = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  )
  return Number.isFinite(computed) && computed > 0 ? computed : 16
}

const ResizableContent: FC<ResizableNPanelLayoutProps> = ({ panels }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  // Memoize the fixed/flex split so downstream useMemo/useCallback deps
  // don't churn every render (`panels.slice(0, -1)` returns a new array
  // each time, which would force the ResizeObserver effect to recreate
  // its observer on every width update).
  const fixedPanels = useMemo(() => panels.slice(0, -1), [panels])
  const flexPanel = useMemo(() => panels[panels.length - 1], [panels])

  const groups = useMemo(
    () => groupContiguousByCollapseGroup(fixedPanels),
    [fixedPanels]
  )

  // Derive panel identity (id + component + weight + minWidthRem + group)
  // for ALL panels including the trailing one so swapping any of those
  // fields resets widths AND collapsed-group state to the new layout's
  // defaults instead of leaking stale state. `component` is included so a
  // step that reuses an id but swaps the panel kind also triggers reset.
  const panelIdentityKey = useMemo(
    () =>
      panels
        .map(
          (p) =>
            `${p.id}:${p.component ?? ''}:${p.weight}:${p.minWidthRem}:${p.collapseGroup ?? ''}`
        )
        .join('|'),
    [panels]
  )

  const remPx = useRemPx(panelIdentityKey)

  const [widths, setWidths] = useState<number[]>(() =>
    fixedPanels.map((p) => p.minWidthRem * 16)
  )
  // Mirror committed widths to a ref so `reallocate` can read the latest
  // value without taking `widths` as a dep -- otherwise every drag-move
  // would re-create `reallocate` and tear down the ResizeObserver.
  const widthsRef = useRef(widths)
  widthsRef.current = widths
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const prevPanelIdentityKey = useRef(panelIdentityKey)
  const hasUserDragged = useRef(false)
  const draggingRef = useRef<{
    handleIndex: number
    startX: number
    startWidth: number
  } | null>(null)

  const onPointerDown = useCallback(
    (handleIndex: number) => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      draggingRef.current = {
        handleIndex,
        startX: event.clientX,
        startWidth: widths[handleIndex],
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [widths]
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragging = draggingRef.current
      if (!dragging) return
      const container = containerRef.current
      if (!container) return

      const min = fixedPanels[dragging.handleIndex].minWidthRem * remPx
      const max = maxWidthForFixedPanel({
        fixedPanels,
        flexPanel,
        groups,
        collapsedGroups,
        containerWidth: container.clientWidth,
        remPx,
        currentWidths: widths,
        draggedIndex: dragging.handleIndex,
      })

      const delta = event.clientX - dragging.startX
      const next = Math.min(max, Math.max(min, dragging.startWidth + delta))
      setWidths((prev) => {
        if (prev[dragging.handleIndex] === next) return prev
        // Flip hasUserDragged only on actual width change so a plain
        // click on a handle (no drag) doesn't permanently switch
        // future container/collapse resizes into clamp-only mode.
        hasUserDragged.current = true
        const copy = [...prev]
        copy[dragging.handleIndex] = next
        return copy
      })
    },
    [collapsedGroups, fixedPanels, flexPanel, groups, remPx, widths]
  )

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return
      draggingRef.current = null
      try {
        event.currentTarget.releasePointerCapture(event.pointerId)
      } catch {
        // Pointer may already be released; ignore.
      }
    },
    []
  )

  const reallocate = useCallback(
    (nextCollapsed: ReadonlySet<string>) => {
      const container = containerRef.current
      if (!container) return
      const containerWidth = container.clientWidth
      if (containerWidth <= 0) return

      const currentWidths = widthsRef.current
      if (hasUserDragged.current) {
        const { widths: next, warn } = clampWidthsToFit({
          fixedPanels,
          flexPanel,
          groups,
          collapsedGroups: nextCollapsed,
          containerWidth,
          remPx,
          currentWidths,
        })
        if (warn) console.warn('[ResizableNPanelLayout]', warn)
        setWidths((prev) => (arraysEqual(prev, next) ? prev : next))
      } else {
        const { widths: next, warn } = allocatePanelWidths({
          fixedPanels,
          flexPanel,
          groups,
          collapsedGroups: nextCollapsed,
          containerWidth,
          remPx,
          prevWidths: currentWidths,
        })
        if (warn) console.warn('[ResizableNPanelLayout]', warn)
        setWidths((prev) => (arraysEqual(prev, next) ? prev : next))
      }
    },
    [fixedPanels, flexPanel, groups, remPx]
  )

  const toggleGroup = useCallback(
    (group: string) => {
      const next = new Set(collapsedGroups)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      setCollapsedGroups(next)
      reallocate(next)
    },
    [collapsedGroups, reallocate]
  )

  // When the layout identity changes (different step / different panel
  // set), reset widths AND collapsed-group state AND user-drag tracking
  // so the new layout starts in fresh-distribute mode.
  useEffect(() => {
    if (prevPanelIdentityKey.current === panelIdentityKey) return
    prevPanelIdentityKey.current = panelIdentityKey
    setCollapsedGroups(new Set())
    hasUserDragged.current = false
    setWidths(fixedPanels.map((p) => p.minWidthRem * remPx))
    // The ResizeObserver effect will run allocation on the next frame
    // once the container is measurable.
  }, [panelIdentityKey, fixedPanels, remPx])

  // ResizeObserver drives both the initial mount allocation (first
  // measurable frame) and post-mount container resizes. Idempotent calls
  // are filtered inside `reallocate`.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (typeof ResizeObserver === 'undefined') {
      // Test/SSR fallback: run once against whatever clientWidth is.
      reallocate(collapsedGroups)
      return
    }
    const observer = new ResizeObserver(() => {
      reallocate(collapsedGroups)
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [collapsedGroups, reallocate])

  return (
    <Container ref={containerRef}>
      {groups.map((group) => {
        const isCollapsible = group.collapseGroup !== null
        const isCollapsed =
          isCollapsible && collapsedGroups.has(group.collapseGroup!)

        if (isCollapsed) {
          return (
            <CollapsedStrip
              key={`g:${group.collapseGroup}`}
              type='button'
              aria-label={`Expand ${group.collapseGroup}`}
              onClick={() => toggleGroup(group.collapseGroup!)}>
              <ChevronRightIcon />
            </CollapsedStrip>
          )
        }

        const lastIndexInGroup = group.indices[group.indices.length - 1]
        const lastPanelInGroup = fixedPanels[lastIndexInGroup]
        const TrailingHandle = isCollapsible
          ? CollapsibleResizeHandle
          : ResizeHandle

        return (
          <CollapsibleGroupWrapper
            key={group.collapseGroup ?? `panel:${group.indices.join(',')}`}>
            {group.indices.map((panelIdx, posInGroup) => {
              const panel = fixedPanels[panelIdx]
              const isLastInGroup = posInGroup === group.indices.length - 1
              return (
                <React.Fragment key={panel.id}>
                  <FixedPanel
                    $width={widths[panelIdx]}
                    $minWidth={panel.minWidthRem * remPx}>
                    {panel.content}
                  </FixedPanel>
                  {!isLastInGroup && (
                    <ResizeHandle
                      onPointerDown={onPointerDown(panelIdx)}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                      role='separator'
                      aria-orientation='vertical'
                      aria-label={`Resize ${panel.id}`}
                    />
                  )}
                </React.Fragment>
              )
            })}
            <TrailingHandle
              onPointerDown={onPointerDown(lastIndexInGroup)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              role='separator'
              aria-orientation='vertical'
              aria-label={`Resize ${lastPanelInGroup.id}`}>
              {isCollapsible && (
                <CollapseChevronOverlay
                  type='button'
                  aria-label={`Collapse ${group.collapseGroup}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => toggleGroup(group.collapseGroup!)}>
                  <ChevronLeftIcon />
                </CollapseChevronOverlay>
              )}
            </TrailingHandle>
          </CollapsibleGroupWrapper>
        )
      })}
      <FlexPanel $minWidth={flexPanel.minWidthRem * remPx}>
        {flexPanel.content}
      </FlexPanel>
    </Container>
  )
}

function arraysEqual(a: ReadonlyArray<number>, b: ReadonlyArray<number>) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
